import React from "react";
import { Rnd } from "react-rnd";
import { useWindowManager } from "../../lib/windowManager/WindowManagerContext";

export default function WindowFrame({ id, title, isDesktop, children }) {
  const { windows, closeWindow, minimizeWindow, focusWindow, moveWindow, resizeWindow, toggleMaximize } =
    useWindowManager();
  const state = windows[id];
  if (!state) return null;

  // Mobile / SSR baseline: a plain section in a normal scrolling page — no drag,
  // no "closed" concept. Also the crawlable, keyboard-simple default.
  if (!isDesktop) {
    return (
      <section className="win-window win98-stacked-window" aria-label={title}>
        <div className="win-titlebar">
          <span>{title}</span>
        </div>
        <div className="win98-window-body" data-window-body={id}>
          {children}
        </div>
      </section>
    );
  }

  if (state.status !== "open") return null;

  const titlebar = (
    <div className="win-titlebar win98-drag-handle" onDoubleClick={() => toggleMaximize(id)}>
      <span className="win98-titlebar-label">{title}</span>
      <div className="win98-titlebar-buttons">
        <button
          type="button"
          className="win-btn win-btn-icon"
          aria-label={`Minimize ${title}`}
          onClick={() => minimizeWindow(id)}
        >
          _
        </button>
        <button
          type="button"
          className="win-btn win-btn-icon"
          aria-label={state.maximized ? `Restore ${title}` : `Maximize ${title}`}
          onClick={() => toggleMaximize(id)}
        >
          {state.maximized ? "❐" : "□"}
        </button>
        <button
          type="button"
          className="win-btn win-btn-icon"
          aria-label={`Close ${title}`}
          onClick={() => closeWindow(id)}
        >
          ✕
        </button>
      </div>
    </div>
  );

  // Maximized: a fixed full-desktop div rendered OUTSIDE react-rnd so its flex
  // column is honored and the body scrolls correctly.
  if (state.maximized) {
    return (
      <div
        className="win-window win98-floating-window win98-window--maximized"
        style={{ zIndex: state.zIndex }}
        onMouseDown={() => focusWindow(id)}
      >
        {titlebar}
        <div className="win98-window-body" data-window-body={id}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <Rnd
      size={{ width: state.width, height: state.height }}
      position={{ x: state.x, y: state.y }}
      onDragStart={() => focusWindow(id)}
      onDragStop={(_e, d) => moveWindow(id, d.x, d.y)}
      onResizeStop={(_e, _dir, ref, _delta, pos) => {
        resizeWindow(id, ref.offsetWidth, ref.offsetHeight);
        moveWindow(id, pos.x, pos.y);
      }}
      onMouseDown={() => focusWindow(id)}
      dragHandleClassName="win98-drag-handle"
      minWidth={300}
      minHeight={220}
      bounds="parent"
      // MUST be inline — react-rnd's own inline style would otherwise clobber a
      // class-based display, leaving the body unable to scroll.
      style={{ zIndex: state.zIndex, display: "flex", flexDirection: "column" }}
      className="win-window win98-floating-window"
    >
      {titlebar}
      <div className="win98-window-body" data-window-body={id}>
        {children}
      </div>
    </Rnd>
  );
}
